import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';
import AppNotificationsBillingComponent from 'webapp/components/app-notifications/billing';
import AppNotificationsCardExpirationComponent from 'webapp/components/app-notifications/card-expiration';
import AppNotificationsDemoUserComponent from 'webapp/components/app-notifications/demo-user';
import AppNotificationsFreeNotificationComponent from 'webapp/components/app-notifications/free-notification';
import AppNotificationsInactiveUserComponent from 'webapp/components/app-notifications/inactive-user';
import AppNotificationsLegacyMetricsComponent from 'webapp/components/app-notifications/legacy-metrics';
import AppNotificationsPromotionsGrowth2024DiscountComponent from 'webapp/components/app-notifications/promotions/growth2024-discount';
import AppNotificationsPromotionsGrowth2024TrialComponent from 'webapp/components/app-notifications/promotions/growth2024-trial';
import AppNotificationsStatusComponent from 'webapp/components/app-notifications/status';
import AppNotificationsTrialComponent from 'webapp/components/app-notifications/trial';
import AppNotificationsVerifyComponent from 'webapp/components/app-notifications/verify';
import AppNotificationsYearlyUpgradeComponent from 'webapp/components/app-notifications/yearly-upgrade';

import type AppNotifications from 'webapp/services/app-notifications';

export default class AppNotificationsComponent extends Component {
	@service declare appNotifications: AppNotifications;

	heightObserver: ResizeObserver | null = null;

	@action
	sendResizeEvent(): void {
		window.dispatchEvent(new Event('resize'));
	}

	@action
	closeVerifyNotification(): void {
		this.appNotifications.skipVerifyNotification = true;
	}

	@action
	closeYearlyUpgradeNotification(): void {
		this.appNotifications.skipYearlyUpgradeNotification = 1;
	}

	@action
	closeInactiveUsersNotification(): void {
		this.appNotifications.skipInactiveUsersNotification = true;
	}

	@action
	closeLegacyMetricsNotification(): void {
		this.appNotifications.skipLegacyMetricsNotification = true;
	}

	@action
	hideNotifications(): void {
		this.appNotifications.skipAllNotifications = true;
	}

	@action
	observeHeight(element: HTMLElement): void {
		this.heightObserver = new ResizeObserver(() => {
			document.documentElement.style.setProperty(
				'--app-notifications-height',
				`${Math.ceil(element.getBoundingClientRect().height)}px`,
			);
		});
		this.heightObserver.observe(element);
	}

	override willDestroy(): void {
		super.willDestroy();
		this.heightObserver?.disconnect();
		document.documentElement.style.removeProperty('--app-notifications-height');
	}
	<template>
		<div
			id="app-notifications"
			{{didInsert this.sendResizeEvent}}
			{{didInsert this.observeHeight}}
			{{willDestroy this.sendResizeEvent}}
		>
			{{#if this.appNotifications.showDemoNotification}}
				<AppNotificationsDemoUserComponent />
			{{else if this.appNotifications.showBillingNotification}}
				<AppNotificationsBillingComponent @onClose={{this.hideNotifications}} />
			{{else if this.appNotifications.showVerifyNotification}}
				<AppNotificationsVerifyComponent @onClose={{this.closeVerifyNotification}} />
			{{else if this.appNotifications.showSystemStatusNotification}}
				<AppNotificationsStatusComponent @onClose={{this.hideNotifications}} />
			{{else if this.appNotifications.showCardExpirationNotification}}
				<AppNotificationsCardExpirationComponent @onClose={{this.hideNotifications}} />
			{{else if this.appNotifications.showFreeNotification}}
				<AppNotificationsFreeNotificationComponent @onClose={{this.hideNotifications}} />
			{{else if this.appNotifications.showInactiveUsersNotification}}
				<AppNotificationsInactiveUserComponent
					@onClose={{this.closeInactiveUsersNotification}}
				/>
			{{else if this.appNotifications.showTrialNotification}}
				<AppNotificationsTrialComponent @onClose={{this.hideNotifications}} />
			{{else if this.appNotifications.showYearlyUpgradeNotification}}
				<AppNotificationsYearlyUpgradeComponent
					@onClose={{this.closeYearlyUpgradeNotification}}
				/>
			{{else if this.appNotifications.showLegacyMetricsNotification}}
				<AppNotificationsLegacyMetricsComponent
					@onClose={{this.closeLegacyMetricsNotification}}
				/>
			{{else if this.appNotifications.showGrowth2024TrialNotification}}
				<AppNotificationsPromotionsGrowth2024TrialComponent />
			{{else if this.appNotifications.showGrowth2024DiscountNotification}}
				<AppNotificationsPromotionsGrowth2024DiscountComponent />
			{{/if}}
		</div>
	</template>
}
